import { plainText } from "../../atoms/text/text";
import type {
  ChannelKind,
  LineupCategory,
  LineupChannel,
  LineupColumn,
  LineupRow,
  LineupSection,
  LineupSubscriptionRow,
  Membership
} from "./channel-lineup.types";
import { MEMBERSHIPS } from "./indicator.shared";
import { isAddonAnywhere, isMyFreeRow } from "./membership.shared";
import { parseLineupSearchId } from "./lineup-search.shared";
import {
  LINEUP_SORT_VALUES,
  type LineupSortValue,
  SORT_VALUE_AZ,
  SORT_VALUE_CATEGORY,
  SORT_VALUE_NUMBER,
  isLineupSortValue
} from "./sort-radios.shared";

/**
 * Signature grid — which sections exist, in what order, which rows are in them and which start open
 * (GS1-GS7, GS10, GS12, GS13). Return-pure, and no React import on purpose.
 */

/**
 * Which shelf a section is, independent of its wording. GS1's MyFree label is content-managed and
 * GS3 renames the add-on one, so matching a section on its heading would silently drop GS9's legend
 * and GS11's lockup the first time CI changes a word.
 */
export type SectionKind =
  | "subscriptions"
  | "included"
  | "category"
  | "local"
  | "rsn"
  | "addon"
  | "myFree";

/** GS4 — a labelled strip inside the Category view's add-on section. */
export interface LineupSectionGroup {
  label: string;
  rows: LineupRow[];
}

/**
 * Extends `LineupSection`, never replaces it: every `LineupSection` is a valid `LineupGridSection`,
 * so a caller already holding the WAT-12369 shape passes it straight in.
 */
export interface LineupGridSection extends LineupSection {
  /** Which shelf this is. Absent on a section built outside this module,
   *  which then gets neither GS9's legend nor GS11's lockup. */
  kind?: SectionKind;
  /** GS4, Category view's add-on section only. `rows` stays authoritative
   *  for GS6's emptiness test; this is a presentation split of the same
   *  channels, so a channel in two categories appears under both strips. */
  groups?: LineupSectionGroup[];
  /**
   * GS9's key, narrowed to what this section's own rows actually draw — the same rule GQ3 already
   * applies to the search results, in GS9's own line order.
   */
  memberships?: Membership[];
}

/**
 * `LineupModel.platform` (DATA-CONTRACT §2), narrowed to the one rule this module reads it for —
 * GR7's satellite `fastChannel` arm.
 */
export type LineupPlatform = "ott" | "satellite";

export interface BuildSignatureSectionsInput {
  /**
   * `SORT_VALUE_AZ` | `SORT_VALUE_NUMBER` | `SORT_VALUE_CATEGORY`, in any case and with any
   * padding. Anything else is read as A-Z — GH4's first option — and warns outside production
   * (`resolveSort`).
   */
  sort: string;
  /** The grid's columns, in authored order — `cells` is positional against
   *  this array and nothing here re-orders it. */
  columns: LineupColumn[];
  /** Every channel the model has for this lineup, MyFree included. */
  channels: LineupChannel[];
  /**
   * GS3/GS5, blok-authored and in authored order. Category sort with none authored collapses to the
   * A-Z grouping rather than rendering nothing. A label repeated (trimmed, case-folded) is dropped
   * and reported — `dedupeCategories`.
   */
  categories?: LineupCategory[];
  /** GS1 — the section shows only when subscriptions are enabled and exist,
   *  which is simply "this array has entries". */
  subscriptionRows?: LineupSubscriptionRow[];
  /** GS1 — the MyFree section's heading. */
  myFreeLabel?: string;
  /** GS1 — the subscriptions section's heading. */
  subscriptionsHeading?: string;
  /** GS1 — the included channels section's heading. */
  includedHeading?: string;
  /** GS1 — the add-on section's heading. */
  addonHeading?: string;
  /** GS3 — the Category view's local channels section heading. */
  localHeading?: string;
  /** GS3 — the Category view's regional sports section heading. */
  rsnHeading?: string;
  /**
   * GR7's amendment — "MyFree rows are `channels.filter(myFree)` plus the satellite `fastChannel`
   * rule" (DATA-CONTRACT §3).
   */
  platform?: LineupPlatform;
  /** Rows reused across builds (`createSignatureRowPool`). Absent, a fresh
   *  pool is made per call — correct, but every build yields new row objects. */
  rowPool?: SignatureRowPool;
}

/**
 * The arms this module does not implement, quoted so the PR and the component's own TSDoc cannot
 * drift from each other.
 */
export const SIGNATURE_GRID_DIVERGENCES = {
  g5IncludedWithBundledAddon:
    "G5 — CLOSED (WAT-12166), and closed UPSTREAM of this module rather than " +
    "in it. A channel included via a BUNDLED ADD-ON IS placed in Included " +
    "channels: channels-service still carries addons[].includedWith as plan " +
    "DISPLAY NAMES, but the model now resolves those to billingProductCodes " +
    "from the feed's own signature/genre refs, so what reaches this file is " +
    "already an `included` entry in LineupChannel.packages. The rule above " +
    "is unchanged — this module still joins on codes only and matches no " +
    "name — which is exactly why the fix had to land where the names are.",
  gs3SportsCollapse:
    "GS3 — \"if several categories are marked as sports, only the first one " +
    "shows, as National Sports\" HAS an input, and is still not ported. " +
    "Legacy marks a category as sports by substring " +
    "(channel-lineup-page-adapter.ts:280-281 filters categoryConfig on " +
    "engCategories.some(e => e.toLowerCase().includes(\"sport\"))), then keeps " +
    "sportsCategory[0] and relabels it \"National Sports\" (:319-324). " +
    "engCategories is this type's `matches`, so the rule reads. ⚠ ITS SCOPE " +
    "OBJECTION IS NOW ANSWERED, and the answer is the one this entry named " +
    "third: legacy scans only the content-managed genre categories because " +
    "Local channels and Regional Sports are structural rows inserted " +
    "separately (:325-331, {key:\"LOCAL\"} and {key:\"RSN\"}), and WAT-12498 " +
    "gives LineupCategory a `kind` so a structural row is identified by SHAPE " +
    "and kept out of the genre list before anything reads it " +
    "(`genreCategories` below). A substring run over that list can no longer " +
    "match \"Regional Sports\" first. What is still NOT ported is the collapse " +
    "itself — it is a second substring heuristic over author-typed `matches`, " +
    "with no feed field behind it, and it belongs with WAT-12323's resolver " +
    "rather than with a rule invented here. Until then the authored genre " +
    "list is taken as given and nothing collapses.",
  localKindVsLocalCategory:
    "GS3/GS5 vs GR4 — CLOSED IN THE CATEGORY VIEW (WAT-12498), and the fix " +
    "is that `kind` now decides the section there, not `categories[]`. It " +
    "used to read: a local whose `categories[]` says \"Entertainment\" sits " +
    "in the Entertainment section carrying a Local badge and never reaches a " +
    "\"Local channels\" bucket — CHANNEL_LINEUP_CHANNELS.TELEMUNDO_EAST_MARKUP " +
    "is exactly that shape and the live list has it. The Category view now " +
    "buckets every local and RSN structurally and excludes them from the " +
    "genre sections, which is v2's own rule: bucketChannelsByCategory " +
    "(lib/helper/channels-service-helpers.ts:489) skips a channel carrying " +
    "isLocal/isRsn outright, so v2 shows those stations under LOCAL/RSN only. " +
    "⚠ STILL OPEN IN A-Z AND CHANNEL NUMBER, where there are no categories at " +
    "all and a local sits in the one flat Included section — which is also " +
    "what v2 does (isChannelInAnyCategory:580 admits a local unconditionally), " +
    "so the two views agree with production rather than with each other."
} as const;

/* An inline `{ sensitivity: "base" }` options object forces V8 off its fast `localeCompare` path
   and builds a fresh `Intl.Collator` on every comparison — ~2,800 of them for one 340-row sort,
   measured at 37.6 ms (93% of `buildSignatureSections`). */
const NAME_COLLATOR = new Intl.Collator(undefined, { sensitivity: "base" });

function byName(a: LineupChannel, b: LineupChannel): number {
  /* GS2 — "alphabetically by channel name, not case-sensitive". `base`
     sensitivity also folds accents, which is what a reader scanning an
     alphabetical list expects of "ViX" against "Vix". */
  return NAME_COLLATOR.compare(a.name, b.name);
}

function numericValue(channel: LineupChannel): number {
  const parsed = Number.parseInt(channel.number ?? "", 10);
  /* A channel with no number, or a non-numeric one, sorts after every
     numbered channel rather than at 0 — GS2 says "lowest first", and an
     absent number is not a low one. */
  return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
}

function byNumber(a: LineupChannel, b: LineupChannel): number {
  const difference = numericValue(a) - numericValue(b);
  /* GS2 — "rows that share a number are ordered by name". */
  return difference === 0 ? byName(a, b) : difference;
}

/** A copy, never in place: the caller's array is the model's, and sorting it
 *  would reorder the same objects for every other consumer. */
function sortRowsBy(
  rows: LineupRow[],
  compare: (a: LineupChannel, b: LineupChannel) => number
): LineupRow[] {
  return [...rows].sort((a, b) => compare(a.channel, b.channel));
}

/** GS2 — Channel number orders by number, every other sort by name. */
function sortRows(rows: LineupRow[], sort: LineupSortValue): LineupRow[] {
  return sortRowsBy(rows, sort === SORT_VALUE_NUMBER ? byNumber : byName);
}

/** GS10/GR2 — one cell per column, read off the model's own membership map
 *  by `billingProductCode`. `null` is "no relationship to that column". */
export function cellsFor(channel: LineupChannel, columns: LineupColumn[]): (Membership | null)[] {
  const packages = channel.packages ?? {};
  return columns.map(column => packages[column.billingProductCode] ?? null);
}

/** GS10 — "in Included channels it shows only its ✓ cells". */
function includedCellsOnly(cells: (Membership | null)[]): (Membership | null)[] {
  return cells.map(cell => (cell === "included" ? cell : null));
}

function has(cells: (Membership | null)[], ...wanted: Membership[]): boolean {
  return cells.some(cell => cell !== null && wanted.includes(cell));
}

/**
 * GS9 / GQ3 — "only the indicator types present", for one set of rows. Filtered out of
 * `MEMBERSHIPS` rather than collected into a set and spread, so the key's line order is GS9's
 * whatever order the rows arrived in.
 */
function membershipsIn(
  rows: readonly LineupRow[],
  seed: readonly Membership[] = []
): Membership[] {
  const present = new Set<Membership>(seed);
  rows.forEach(row => row.cells.forEach(cell => {
    if (cell !== null) {
      present.add(cell);
    }
  }));
  return MEMBERSHIPS.filter(membership => present.has(membership));
}

/** GS10 — "local channels and RSNs never appear as add-ons" means the visitor's in-market RSN.
 *  An out-of-market RSN is sold like a national (promo / add-on), so it stays eligible. */
function canBeAddon(channel: LineupChannel): boolean {
  return channel.kind === "national" || channel.kind === "rsn-oom";
}

/**
 * Which structural section a channel belongs to, or `null` for one that goes through the authored
 * categories. An out-of-market RSN is not the visitor's RSN (GP17), so it is filed like a national.
 * Exhaustive on purpose: a new kind fails to compile here instead of landing in "rsn".
 */
function structuralKindOf({ kind }: { kind: ChannelKind }): "local" | "rsn" | null {
  switch (kind) {
    case "national":
    case "rsn-oom":
      return null;
    case "local":
      return "local";
    case "rsn":
      return "rsn";
    default: {
      // Compile-time exhaustiveness; at runtime an unknown kind is filed like a national.
      const unreachable: never = kind;
      void unreachable;
      return null;
    }
  }
}

function matchesCategory(channel: LineupChannel, category: LineupCategory): boolean {
  /* Case- and whitespace-tolerant on purpose: `matches` is typed by a CMS author and `categories[]`
     comes from channels-service, so the two sides are maintained by different people and drift in
     casing long before they drift in meaning. */
  const authored = foldedMatches(category);
  return channel.categories.some(name => authored.has(name.trim().toLowerCase()));
}

/* Folded once per category object, not once per row × category. */
const FOLDED_MATCHES = new WeakMap<LineupCategory, Set<string>>();
function foldedMatches(category: LineupCategory): Set<string> {
  let folded = FOLDED_MATCHES.get(category);
  if (!folded) {
    folded = new Set(category.matches.map(match => match.trim().toLowerCase()));
    FOLDED_MATCHES.set(category, folded);
  }
  return folded;
}

/**
 * One row object per channel and variant, reused across every build with the same columns, so a
 * sort hands React the same `row` and a memoised `ChannelRow` bails out instead of re-rendering
 * (the grid reorders, not remounts).
 */
export interface SignatureRowPool {
  /** All of the channel's cells — the add-on section's rows. */
  raw: (channel: LineupChannel) => LineupRow;
  /** ✓ cells only — Included, the category buckets and the structural rows. */
  included: (channel: LineupChannel) => LineupRow;
  /** ✓ under every column — GR7's MyFree rows. */
  myFree: (channel: LineupChannel) => LineupRow;
}

export function createSignatureRowPool(columns: LineupColumn[]): SignatureRowPool {
  const raw = new WeakMap<LineupChannel, LineupRow>();
  const included = new WeakMap<LineupChannel, LineupRow>();
  const myFree = new WeakMap<LineupChannel, LineupRow>();
  const pooled = (
    cache: WeakMap<LineupChannel, LineupRow>,
    make: (channel: LineupChannel) => LineupRow
  ) => (channel: LineupChannel) => {
    let row = cache.get(channel);
    if (!row) {
      row = make(channel);
      cache.set(channel, row);
    }
    return row;
  };
  const rawRow = pooled(raw, channel => ({
    channel,
    cells: cellsFor(channel, columns)
  }));
  return {
    raw: rawRow,
    included: pooled(included, channel => ({
      channel,
      cells: includedCellsOnly(rawRow(channel).cells)
    })),
    myFree: pooled(myFree, channel => ({
      channel,
      cells: columns.map(() => "included" as Membership)
    }))
  };
}

function section(
  kind: SectionKind,
  heading: string,
  rows: LineupRow[],
  extra?: Partial<LineupGridSection>
): LineupGridSection {
  return {
    kind,
    heading,
    defaultOpen: false,
    rows,
    ...extra
  };
}

/** GS7, read positionally rather than by section name. An interpretation, flagged as one. */
function applyDefaultOpen(sections: LineupGridSection[], grouped: boolean): LineupGridSection[] {
  const contentOpen = grouped ? 2 : 1;
  let opened = 0;

  return sections.map(entry => {
    const isSubscriptions = entry.kind === "subscriptions";
    if (isSubscriptions) {
      return {
        ...entry,
        defaultOpen: true
      };
    }
    if (opened < contentOpen) {
      opened += 1;
      return {
        ...entry,
        defaultOpen: true
      };
    }
    return {
      ...entry,
      defaultOpen: false
    };
  });
}

/** GS6 — "a section is shown only if it has at least one row". Tested on the
 *  DATA, so a section can never survive by rendering something. */
function nonEmpty(sections: LineupGridSection[]): LineupGridSection[] {
  return sections.filter(
    entry => entry.rows.length > 0 || (entry.subscriptionRows?.length ?? 0) > 0
  );
}

function myFreeSection(
  channels: LineupChannel[],
  pool: SignatureRowPool,
  label: string,
  sort: LineupSortValue
): LineupGridSection {
  /* GR7, prod parity — ✓ under every package whatever the memberships say, as v2
     forces it (`chlupStore.ts:390`); a MyFree row's add-on refs never show here. */
  const rows: LineupRow[] = channels.map(pool.myFree);
  /* GS2 — "within the MyFree section, rows follow the chosen sort too". */
  return section("myFree", label, sortRows(rows, sort));
}

/**
 * GS5 names this one category outright — "shown under Entertainment, or under the first category if
 * there is no Entertainment" — so it is the single authored label this module is allowed to know,
 * and the no-name-matching rule in the header is about channel identity, not about it.
 */
const GS5_FALLBACK_CATEGORY_LABEL = "entertainment";

/** The authored category list is the one authored list nothing dedupes. */
function dedupeCategories(categories: LineupCategory[]): LineupCategory[] {
  const kept: LineupCategory[] = [];
  const dropped: string[] = [];
  const seen = new Set<string>();

  for (const category of categories) {
    const folded = category.label.trim().toLowerCase();
    if (seen.has(folded)) {
      dropped.push(category.label);
      continue;
    }
    seen.add(folded);
    kept.push(category);
  }

  /* Dev-only and `[ChannelLineup]`-prefixed — the same gate and prefix `resolveSort` carries below,
     for the same reason it gives: this runs inside a client `useMemo`, where a production warn
     reaches a reader's console and no pod log. */
  if (process.env.NODE_ENV !== "production" && dropped.length > 0) {
    console.warn(
      `[ChannelLineup] dropped ${dropped.length} duplicate category ` +
        `label${dropped.length === 1 ? "" : "s"} (${dropped.join(", ")}). ` +
        "A repeated label renders the same rows twice under two identical " +
        "headings, so only the first is kept — along with its `matches`, " +
        "which means a second row's match list is dropped with it."
    );
  }

  return kept;
}

function categorySections(rows: LineupRow[], categories: LineupCategory[]): LineupGridSection[] {
  const buckets = new Map<string, LineupRow[]>(categories.map(category => [category.label, []]));
  /* GS5 — the authored Entertainment bucket, compared the same way
     `matchesCategory` compares a CMS string (trimmed, case-folded, never a
     substring); otherwise the first authored entry. */
  const fallback =
    categories.find(
      category => category.label.trim().toLowerCase() === GS5_FALLBACK_CATEGORY_LABEL
    ) ?? categories[0];

  rows.forEach(row => {
    const matched = categories.filter(category => matchesCategory(row.channel, category));
    /* GS3 — "a channel appears in every category it belongs to". An out-of-market RSN gets no GS5
       fallback, as prod: `lib/helper/channels-service-helpers` `bucketChannelsByCategory` leaves it
       out, and only `applyEntertainmentCategoryFallback` files unmatched nationals. */
    const noFallback = row.channel.kind === "rsn-oom";
    const unmatched = noFallback ? [] : [fallback];
    const placements = matched.length > 0 ? matched : unmatched;
    placements.forEach(category => {
      buckets.get(category.label)?.push(row);
    });
  });

  /* GS3 — "rows are alphabetical within each section", whatever the sort. */
  return categories.map(category => {
    const bucket = sortRowsBy(buckets.get(category.label) ?? [], byName);
    return section("category", category.label, bucket, { memberships: membershipsIn(bucket) });
  });
}

/**
 * The structural rows, in production's order: Local channels, then Regional Sports
 * (`channel-lineup-page-adapter.ts:324-331`, and the live prod `channelData.gridCategories[1..2]`).
 */
const STRUCTURAL_ORDER = ["local", "rsn"] as const;

type StructuralKind = (typeof STRUCTURAL_ORDER)[number];

/**
 * Each structural section's heading: a category row carrying that shelf's `kind`, else the blok's
 * own heading field. Found by `kind`, never by label (`LineupCategory.kind`).
 */
function structuralLabels(
  categories: LineupCategory[],
  headings: Record<StructuralKind, string>
): Record<StructuralKind, string> {
  const labelFor = (kind: StructuralKind): string => (categories.find(category => category.kind === kind)?.label ?? "").trim() || headings[kind].trim();
  return {
    local: labelFor("local"),
    rsn: labelFor("rsn")
  };
}

/** GS3 — the two sections the ZIP decides, ahead of the authored categories. */
/**
 * Whether an authored row is a Genre bucket this module may place channels in. Two tests, and the
 * second is not a duplicate of `dedupeCategories`.
 */
function isGenreCategory(category: LineupCategory, reservedHeadings: Set<string>): boolean {
  if ((category.kind ?? "genre") !== "genre") {
    return false;
  }
  return !reservedHeadings.has(category.label.trim().toLowerCase());
}

function structuralSections(
  rows: readonly LineupRow[],
  labels: Record<StructuralKind, string>,
  pool: SignatureRowPool
): LineupGridSection[] {
  return STRUCTURAL_ORDER.map(kind => {
    const bucket = rows
      .filter(row => structuralKindOf(row.channel) === kind && !row.channel.myFree)
      .map(row => pool.included(row.channel));
    /* GS3 — "rows are alphabetical within each section", whatever the sort,
       which is also what v2 sorts these two by (`chlupStore.ts:1153-1154`
       applies `Channels.sortAlphabetically` to both arrays). */
    return section(kind, labels[kind], sortRowsBy(bucket, byName));
  });
}

function addonGroups(rows: LineupRow[], categories: LineupCategory[]): LineupSectionGroup[] {
  /* GS4 — strips follow the category order and a channel appears under every category it belongs
     to. */
  return categories
    .map(category => ({
      label: category.label,
      rows: rows.filter(row => matchesCategory(row.channel, category))
    }))
    .filter(group => group.rows.length > 0);
}

/** The one place a free-text sort becomes one of the three values this module buckets by. */
function resolveSort(rawSort: string): LineupSortValue {
  const normalised = rawSort.trim().toLowerCase();
  if (isLineupSortValue(normalised)) {
    return normalised;
  }

  if (process.env.NODE_ENV !== "production" && normalised !== "") {
    console.warn(
      `[ChannelLineup] sort "${rawSort}" is not in the closed vocabulary ` +
        `(${LINEUP_SORT_VALUES.join(", ")}); the grid is bucketed as ` +
        `"${SORT_VALUE_AZ}". The sort radio still shows the authored option as ` +
        "checked, so the two will disagree on screen."
    );
  }

  return SORT_VALUE_AZ;
}

/**
 * GS1-GS7, GS10, GS12 and GS13 in one pass. Returns the sections to render, in order, each with its
 * GS7 default — or an empty array when there is nothing at all, which is GS6's "the grid shows the
 * same message as GQ5".
 */
export function buildSignatureSections({
  sort: rawSort,
  columns,
  channels,
  categories: authoredCategories = [],
  subscriptionRows = [],
  myFreeLabel = "",
  subscriptionsHeading = "",
  includedHeading = "",
  addonHeading = "",
  localHeading = "",
  rsnHeading = "",
  platform = "ott",
  rowPool
}: BuildSignatureSectionsInput): LineupGridSection[] {
  const sort = resolveSort(rawSort);
  const pool = rowPool ?? createSignatureRowPool(columns);
  /* The two structural headings, resolved before the genre list so they can be reserved against it. */
  const structuralHeadings = structuralLabels(authoredCategories, {
    local: localHeading,
    rsn: rsnHeading
  });
  /* The Genre rows alone, and the filter is here rather than at each reader. */
  const reservedHeadings = new Set(
    Object.values(structuralHeadings)
      .filter(label => label !== "")
      .map(label => label.toLowerCase())
  );
  const genreCategories = dedupeCategories(
    authoredCategories.filter(category => isGenreCategory(category, reservedHeadings))
  );

  /* GS12 — a channel listed as MyFree appears only in the MyFree section, in every sort. */
  const myFreeChannels = channels.filter(channel => isMyFreeRow(channel, platform));
  /* Exception to GS12, per production: a MyFree LOCAL (e.g. WABCFAST, 4051)
     also keeps its normal local row; only the structural Local section skips
     it, as v2's structural loop does. */
  const candidates: LineupRow[] = channels
    .filter(channel => !isMyFreeRow(channel, platform) || channel.kind === "local")
    .map(pool.raw);

  /* GS13 — a channel in no Signature package has no row anywhere in this grid. It still appears in
     its Genre Packs, which this module does not build. Applied to the included section only, not to
     the whole grid as it was. */
  const signature: LineupRow[] = candidates.filter(row => row.cells.some(cell => cell !== null));

  const includedRows: LineupRow[] = signature
    .filter(row => has(row.cells, "included"))
    .map(row => pool.included(row.channel));

  /* GS10 — a channel included in one package and an add-on in another is in
     BOTH sections, and here it keeps all of its indicators. A promo-only
     channel belongs here too: the ★ has no other section. */
  const addonRows: LineupRow[] = candidates
    .filter(row => canBeAddon(row.channel) && isAddonAnywhere(row.channel.packages))
    /* GS5 — "an add-on-only channel that matches no category is not shown in any sort". It bites in
       A-Z too, not only where the strips are drawn, so it is applied to the section rather than to
       GS4's grouping. */
    .filter(row => {
      if (genreCategories.length === 0 || has(row.cells, "included")) {
        return true;
      }
      return genreCategories.some(category => matchesCategory(row.channel, category));
    });

  const subscriptions =
    subscriptionRows.length > 0 ?
      [section("subscriptions", subscriptionsHeading, [], { subscriptionRows })] :
      [];

  /* GS3 — Category explodes the single Included channels section into the authored buckets; every
     other sort keeps the one section (GS1). */
  const grouped = sort === SORT_VALUE_CATEGORY && genreCategories.length > 0;
  const body = grouped ?
    /* Nationals only. */
    categorySections(includedRows.filter(row => structuralKindOf(row.channel) === null), genreCategories) :
    [section("included", includedHeading, sortRows(includedRows, sort))];

  /* GS3/GS7 — the ZIP's two sections, Category view only. */
  const structural = grouped ? structuralSections(candidates, structuralHeadings, pool) : [];

  /* GS3 again — the add-on section's own rows are alphabetical in Category
     view, and GS4 splits the same rows into the authored strips. */
  const addonSorted = grouped ? sortRowsBy(addonRows, byName) : sortRows(addonRows, sort);
  const addon = section("addon", addonHeading, addonSorted, {
    /* GS9 under GQ3's rule — "only the indicator types present". */
    memberships: membershipsIn(addonSorted),
    ...(grouped ? { groups: addonGroups(addonSorted, genreCategories) } : {})
  });

  const myFree =
    myFreeChannels.length > 0 ?
      [myFreeSection(myFreeChannels, pool, myFreeLabel, sort)] :
      [];

  /* GS1 / GS3 — the order, and it is PRODUCTION's: subscriptions, the two structural rows, the
     authored categories, add-on, MyFree (`channel-lineup-page-adapter.ts:324-331` builds exactly
     that array and unshifts the subscriptions row onto it). */
  return applyDefaultOpen(
    nonEmpty([...subscriptions, ...structural, ...body, addon, ...myFree]),
    grouped
  );
}

/** GQ1-GQ4 — the grid while a search is active. A different structure, not a filter of the sections. */

/**
 * GQ6 — how the search results arrived. `"partial"` draws its message and the results; `"error"`
 * replaces them. `"partial"` with nothing matched is the AC's "nothing shows" arm and reads as
 * `"error"`, because GQ6 splits on what is on screen.
 */
export type SignatureSearchStatus = "ready" | "partial" | "error";

/** One row of GQ1's merged list. */
export interface SignatureSearchRow extends LineupRow {
  /** GQ4 — decided from the MERGED cells, so a channel included in one package
   *  and an add-on in another is not styled as an add-on. */
  addonStyled: boolean;
}

/** What the search-mode grid draws — GQ1's one list, grouped as GQ4 orders it. */
export interface SignatureSearchResults {
  /** GQ4, first. The grid's own order is kept: GQ1's alphabetical rule names
   *  CHANNELS, and GR6's rows are a short authored list. */
  subscriptionRows: LineupSubscriptionRow[];
  /** GQ4, second — matching Signature channels, merged (GQ2), alphabetical. */
  rows: SignatureSearchRow[];
  /** GQ4, third — matching MyFree channels, drawn under the MyFree label. */
  myFreeRows: SignatureSearchRow[];
  /** GS1's content-managed MyFree wording, resolved. */
  myFreeLabel: string;
  /** GQ3 — "only the indicator types present in the results", in GS9's own
   *  order so the legend's lines cannot reorder with the data. */
  memberships: Membership[];
  /** AX4 — "the count includes subscription and MyFree rows". Counted AFTER
   *  GQ2's merge, because that is what the reader is told about. */
  total: number;
}

export interface BuildSignatureSearchResultsInput extends BuildSignatureSectionsInput {
  /** The seam's de-namespaced CCIDs (`lineup-search.shared.ts`'s
   *  `LineupSearchSelection`), already expanded through ZP12. */
  channelIds: readonly string[];
  /** The seam's de-namespaced Epoch plan codes. */
  subscriptionIds: readonly string[];
}

/** GQ2 — "the best indicator, in the order included, then promo, then add-on". Not `MEMBERSHIPS`. */
const MERGE_PRECEDENCE: readonly Membership[] = ["included", "promo", "addon"];

function betterMembership(a: Membership | null, b: Membership | null): Membership | null {
  if (a === null) {
    return b;
  }
  if (b === null) {
    return a;
  }
  return MERGE_PRECEDENCE.indexOf(a) <= MERGE_PRECEDENCE.indexOf(b) ? a : b;
}

/**
 * GQ2's identity — "the same name (not case-sensitive) and number". A JSON tuple, not a joined
 * string.
 */
function mergeKey(channel: LineupChannel): string {
  return JSON.stringify([channel.name.trim().toLowerCase(), (channel.number ?? "").trim()]);
}

/**
 * GQ4 — "only add-on or promo". An all-null row now reaches here, which is why the channel is read
 * as well as the cells.
 */
function addonStyledFrom(cells: (Membership | null)[], channel: LineupChannel): boolean {
  return (
    !has(cells, "included") && (has(cells, "addon", "promo") || isAddonAnywhere(channel.packages))
  );
}

/** GQ2 — "a channel appears once in the results". */
function mergeSearchRows(rows: LineupRow[]): SignatureSearchRow[] {
  const order: string[] = [];
  /* `Array<…>` rather than `(Membership | null)[]`: the parenthesised union
     inside a type argument trips `func-call-spacing`, which reads the `(` as a
     call. Same type, no suppression. */
  const groups = new Map<string, Array<Membership | null>>();
  const leads = new Map<string, LineupChannel>();

  rows.forEach(row => {
    const key = mergeKey(row.channel);
    const existing = groups.get(key);
    if (existing === undefined) {
      order.push(key);
      groups.set(key, [...row.cells]);
      leads.set(key, row.channel);
      return;
    }
    groups.set(
      key,
      existing.map((cell, index) => betterMembership(cell, row.cells[index] ?? null))
    );
  });

  return order.map(key => {
    const cells = groups.get(key) ?? [];
    const channel = leads.get(key) as LineupChannel;
    return {
      channel,
      cells,
      addonStyled: addonStyledFrom(cells, channel)
    };
  });
}

/**
 * GQ3 — "a legend that lists only the indicator types present in the results", over all three of
 * GQ4's lists at once. `membershipsIn` above is the rule; this adds the one membership that is not
 * a `cells` entry.
 */
function membershipsPresent(
  subscriptionRows: LineupSubscriptionRow[],
  ...rowSets: SignatureSearchRow[][]
): Membership[] {
  /* GR6 — "subscriptions only ever show included (✓), never add-on or promo",
     so a ticked subscription cell is an ✓ in the results too. */
  const seed: Membership[] =
    subscriptionRows.some(row => row.cells.some(Boolean)) ? ["included"] : [];
  return membershipsIn(rowSets.flat(), seed);
}

/**
 * GQ1-GQ4 in one pass, from the same raw input `buildSignatureSections` takes plus the seam's
 * matched ids. `Sort` and `categories` are read for nothing here.
 */
export function buildSignatureSearchResults({
  columns,
  channels,
  channelIds,
  subscriptionIds,
  subscriptionRows = [],
  myFreeLabel = "",
  platform = "ott"
}: BuildSignatureSearchResultsInput): SignatureSearchResults {
  const wantedChannels = new Set(channelIds);
  const wantedSubscriptions = new Set(subscriptionIds);

  const matchedSubscriptions = subscriptionRows.filter(row => wantedSubscriptions.has(row.subscription.id));
  const matched = channels.filter(channel => wantedChannels.has(channel.id));

  /* GS12/GR7 — the same split `buildSignatureSections` uses, which is what makes GQ2's "a
     MyFree-only entry is never merged with a Signature entry" fall out rather than needing a rule
     of its own: the two lists are exact complements and are merged separately. */
  const signatureRows: LineupRow[] = matched
    .filter(channel => !isMyFreeRow(channel, platform))
    .map(channel => ({
      channel,
      cells: cellsFor(channel, columns)
    }))
    /* GS13, with the add-on exemption the browse grid makes and the structural one WAT-12498 adds —
       see the header above. */
    .filter(row => row.cells.some(cell => cell !== null) ||
      isAddonAnywhere(row.channel.packages) ||
      structuralKindOf(row.channel) !== null);

  /* GR7 — "MyFree DIRECTV rows show ✓ under every package", derived from the
     section rather than from the membership map, exactly as `myFreeSection`
     does for browse mode. */
  const myFreeSourceRows: LineupRow[] = matched
    .filter(channel => isMyFreeRow(channel, platform))
    .map(channel => ({
      channel,
      cells: columns.map(() => "included" as Membership)
    }));

  const rows = mergeSearchRows(sortRowsBy(signatureRows, byName));
  const myFreeRows = mergeSearchRows(sortRowsBy(myFreeSourceRows, byName));

  return {
    subscriptionRows: matchedSubscriptions,
    rows,
    myFreeRows,
    myFreeLabel,
    memberships: membershipsPresent(matchedSubscriptions, rows, myFreeRows),
    total: matchedSubscriptions.length + rows.length + myFreeRows.length
  };
}

/** AX4 — "up to 3 terms are named, then ', and N more'". */
const ANNOUNCED_TERM_LIMIT = 3;

export interface SignatureSearchTermsInput {
  /** The seam's chips, in the reader's order. */
  selectedIds: readonly string[];
  /** ZP12 — every row in THIS market a chip stands for, keyed by chip. */
  matchedIds: Record<string, string[]>;
  /** Each chip's own wording, keyed by chip, straight off `LineupSearchSelection`. */
  selectedLabels: Record<string, string>;
  /** The model's channels, for resolving a chip back to a name. */
  channels: readonly LineupChannel[];
  /** The grid's subscription rows, same job for a subscription chip. */
  subscriptionRows?: readonly LineupSubscriptionRow[];
}

/**
 * AX4's `{terms}` — the names behind the chips. The seam carries no typed text. The molecule never
 * reports the query upward (`channel-search.client.tsx:55-59`), so a term is a chip's name and
 * never what was typed.
 */
export function signatureSearchTerms({
  selectedIds,
  matchedIds,
  selectedLabels,
  channels,
  subscriptionRows = []
}: SignatureSearchTermsInput): string[] {
  // Runs on every grid render; with no chip there is nothing to name.
  if (selectedIds.length === 0) {
    return [];
  }
  const channelNames = new Map(channels.map(channel => [channel.id, channel.name]));
  const subscriptionNames = new Map(
    subscriptionRows.map(row => [row.subscription.id, row.subscription.name])
  );
  const terms: string[] = [];
  const seen = new Set<string>();

  /* Feed strings go through `plainText` before they are announced: "Telemundo East<br/>" is a live
     fixture value. */
  const nameTerm = (value: string | undefined): boolean => {
    const term = plainText(value ?? "").trim();
    if (term === "") {
      return false;
    }
    if (!seen.has(term.toLowerCase())) {
      seen.add(term.toLowerCase());
      terms.push(term);
    }
    return true;
  };

  selectedIds.forEach(selectedId => {
    /* The chip's own id first — ZP12 clause 1 has already moved it to the row
       this market carries — then whatever it was expanded to. */
    for (const candidate of [selectedId, ...(matchedIds[selectedId] ?? [])]) {
      const ref = parseLineupSearchId(candidate);
      if (ref === undefined) {
        continue;
      }
      const found = ref.kind === "channel" ? channelNames.get(ref.id) : subscriptionNames.get(ref.id);
      if (nameTerm(found)) {
        return;
      }
    }
    // Nothing in this market resolved — the chip's own wording is the only
    // name left for it.
    nameTerm(selectedLabels[selectedId]);
  });

  return terms;
}

/** AX4 — the named terms as one phrase, or "" when none could be named. */
function termsPhrase(terms: readonly string[]): string {
  const named = terms.slice(0, ANNOUNCED_TERM_LIMIT);
  if (named.length === 0) {
    return "";
  }
  const rest = terms.length - named.length;
  return rest > 0 ? `${named.join(", ")}, and ${rest} more` : named.join(", ");
}

function channelCount(count: number): string {
  /* AX4 writes "{n} channel(s)". `(s)` is requirements shorthand — a screen
     reader would read the brackets aloud — so it is resolved here. */
  return `${count} ${count === 1 ? "channel" : "channels"}`;
}

export interface SignatureSearchAnnouncementInput {
  /** GQ6's state. Browsing announces nothing at all — see the return below. */
  status: SignatureSearchStatus;
  /** SR11 — whether a search is filtering the view. */
  isActive: boolean;
  /** AX4's `{n}` — `SignatureSearchResults.total`, which already counts the
   *  subscription and MyFree rows and is already merged. */
  total: number;
  terms: readonly string[];
  /** GQ5's wording, resolved. */
  noMatchMessage: string;
  /** GQ6's partial wording, resolved. */
  partialMessage: string;
  /** GQ6's total-failure wording, resolved. */
  errorMessage: string;
}

/**
 * AX4 — "a screen reader announces every search change", as one string. GQ6 is tested first, then
 * GQ5, then the results arm.
 */
export function signatureSearchAnnouncement({
  status,
  isActive,
  total,
  terms,
  noMatchMessage,
  partialMessage,
  errorMessage
}: SignatureSearchAnnouncementInput): string {
  if (!isActive) {
    /* Browsing is not a search change. The region still renders (empty), so the platform is already
       watching it when the first search lands — the same reason
       `molecules/channel-search/search-status.tsx` renders its own region unconditionally. */
    return "";
  }

  const phrase = termsPhrase(terms);

  if (status === "error" || status === "partial") {
    /* GQ6'S discriminator is what is on screen, not `status`. */
    const nothingShown = status === "error" || total === 0;
    const message = nothingShown ? errorMessage : partialMessage;
    const clause = phrase === "" ? "" : ` (search: "${phrase}")`;
    const shown = nothingShown ? "" : ` ${channelCount(total)} shown.`;
    return `${message}${clause}${shown}`;
  }

  if (total === 0) {
    return phrase === "" ?
      `${noMatchMessage} No results for your search.` :
      `${noMatchMessage} No results for "${phrase}".`;
  }

  return phrase === "" ?
    `${channelCount(total)} found for your search.` :
    `${channelCount(total)} found for "${phrase}".`;
}
